import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { connect } from 'react-redux'
import {
  makeStyles,
  createStyles,
  Typography,
  Grid,
  Box,
  Checkbox,
  Fade,
  Tooltip,
  withStyles,
} from '@material-ui/core'
import { downloadURI } from '@/utils/utils'
import CMSText, { prismicText } from '../Global/CMSText'
import ImgKitHandler from '../ImgKitHandler/ImgKitHandler'
import Price from '../Shop/Price'
import Button from '../Button/Button'
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace'
import Loader from '../Loader/Loader'
import DownloadLink from '../Global/DownloadLink'
import DownloadInvoiceButton from '../Order/DownloadInvoiceButton/DownloadInvoiceButton'
import { isSubscription } from '@/utils/constants'
import { useTakeback } from '@/hooks/useTakeback'
import { Order } from '@/interfaces/common/contract'

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {},
    hide: {
      opacity: 0,
      transition: theme.transitions.create('opacity'),
    },
    show: {
      opacity: 1,
      transition: theme.transitions.create('opacity'),
    },
    takeBackListTitle: {
      marginBottom: theme.spacing(2),
    },
    takeBackListHeader: {
      display: 'flex',
      justifyContent: 'start',
      alignItems: 'center',
    },
    takeBackItemTitle: {
      paddingLeft: theme.spacing(1),
    },
    circle: {
      width: 16,
      height: 16,
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
    },
    productCard: {
      display: 'flex',
      overflow: 'hidden',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: 60,
      background: theme.palette.common.white,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
    productCardCheckbox: {},
    productCardImg: {
      width: 50,
      height: 50,
      position: 'relative',
      marginRight: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        marginRight: theme.spacing(1),
      },
    },
    productCardContent: {
      flex: 1,
    },
    takeBackItem: {
      borderLeft: `1px solid currentColor`,
      paddingLeft: theme.spacing(2),
      marginLeft: theme.spacing(1),
      paddingBottom: theme.spacing(3),
    },
    takeBackItemActive: {
      borderColor: 'transparent',
      paddingLeft: 0,
      marginLeft: theme.spacing(1),
    },
    takeBackItemLast: {
      borderLeft: 0,
    },
    imgProduct: {
      width: 50,
      height: 50,
      objectFit: 'contain',
      display: 'block',
    },
    productName: {},
    productStatus: {
      marginLeft: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        marginLeft: theme.spacing(1),
      },
    },
    takeBackOrder: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      width: '100%',
    },
    orderDetailsRow: {
      textAlign: 'right',
    },
    orderDetailsBtn: {},
    orderDetailsRowActions: {
      padding: theme.spacing(1, 0, 2),
    },
    simpleButton: {
      padding: 0,
      textTransform: 'inherit',
      color: theme.extraColors.greyText,
      '&:hover, &:focus': {
        background: 'none',
      },
      '& .MuiSvgIcon-root': {
        fontSize: '.8rem',
      },
    },
    simpleButtonPrimary: {
      color: theme.palette.primary.main,
    },
    simpleButtonDanger: {
      color: theme.palette.error.main,
    },
    currentOrderList: {
      padding: theme.spacing(3),
      background: theme.palette.common.white,
    },
    loaderContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '20vh',
    },
    returnCtaContainer: {
      minHeight: '2rem',
    },
    downloadInvoiceButton: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginBottom: theme.spacing(1.5),
    },
  })
)

type Tprops = {
  t: any
  takeback: any
  main: any
  account: any
  orders: Order[]
  user: any
  stores: any
  postProductToReturn: (data: object) => any
  getRightOfWithdrawalInfo: (id: string) => any
  handleDownloadInvoice: (orderToken: string) => any
  invoiceDownloadError: boolean
}

const MainTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    fontSize: 12,
    textAlign: 'center',
    padding: theme.spacing(1),
    width: '100%',
  },
  arrow: {
    color: theme.palette.secondary.main,
  },
}))(Tooltip)

const mapStateToProps = (state) => ({
  user: state.user,
})

const TakeBackOrderList = ({
  t,
  takeback,
  orders,
  main,
  user,
  stores,
  postProductToReturn,
  account,
  getRightOfWithdrawalInfo,
  handleDownloadInvoice,
  invoiceDownloadError,
}: Tprops) => {
  const classes = useStyles()
  const { productCheckState } = useTakeback()
  const states = productCheckState(takeback?.data)
  const [activeOrder, setActiveOrder] = useState(false)
  const [activeOrderLoading, setActiveLoading] = useState(false)
  const [indexOrder, setIndexOrder] = useState(null)
  const [isModifying, setIsModifying] = useState(false)
  const [productSelection, setProductSelection] = useState(false)
  const [productToReturn, setProductToReturn] = useState([])
  const [isDownloadLoading, setIsDownloadLoading] = useState(false)
  const [downloadLink, setDownloadLink] = useState('')
  const warehouseId = stores?.selectedStore?.warehouseId
    ? stores?.selectedStore?.warehouseId
    : process.env.CUSTOM_WAREHOUSE
    ? process.env.CUSTOM_WAREHOUSE
    : null
  const [isOrderReturnable, setIsOrderReturnable] = useState([])
  const tooltipWithdrawalText = prismicText(
    takeback?.data?.order_withdrawal_text,
    t('texts:account:order_take_back_withdrawal_text')
  )

  const getReturnValues = async (idList: string[]) => {
    const responses = await Promise.all(
      idList.map((id) => {
        return getRightOfWithdrawalInfo(id).then((info) => {
          return {
            orderId: id,
            orderInfo: info,
          }
        })
      })
    )
    return responses.map((response) => {
      return {
        orderId: response.orderId,
        returnable: response?.orderInfo?.response
          ?.map((item) => item.returnable)
          .includes(true),
      }
    })
  }

  useEffect(() => {
    const ordersToCheck = orders
      .filter((order) => dayjs().diff(order.checkoutCompletedAt, 'day') <= 45)
      .map((order) => order.number)

    getReturnValues(ordersToCheck).then(
      (result) => setIsOrderReturnable(result)
      // inOrderPushWIP.push(result)
    )
  }, [orders])

  const handleActiveCurrentOrder = (orderIndex) => {
    setActiveLoading(true)
    setIndexOrder(orderIndex)
    setTimeout(() => {
      setActiveOrder(true)
      setActiveLoading(false)
    }, 800)
  }

  const handleBackToOrders = () => {
    setActiveOrder(false)
    setIndexOrder(null)
    setIsModifying(false)
    setProductSelection(false)
    setProductToReturn([])
    setDownloadLink('')
  }

  const cancelReturn = () => {
    // we reset everything
    setIsModifying(false)
    setProductSelection(false)
    setProductToReturn([])
  }

  const handleProductSelection = (
    Sku,
    id,
    quantity,
    token,
    item,
    orderNumber?: string,
    warehouseId?: string,
    carrierId?: number
  ) => {
    setProductSelection(true)
    const productSelectionArray = [...productToReturn]

    // we first check if the product selected is already in the array of selected products
    if (productToReturn.find((product) => product.id === id)) {
      // if it is true the we remove the row in the array and we adapt the prices
      const index = productToReturn.findIndex((product) => product.id === id)
      productSelectionArray.splice(index, 1)
      // if nothing selected, disable generate button
      setProductSelection(productSelectionArray.length !== 0)
    } else {
      // if it is false we push a new row in the array of selected products and we adapt the prices
      productSelectionArray.push({
        id: id,
        Sku: Sku,
        expand: true,
        product: item.variant,
        quantity: quantity,
        orderToken: token,
        orderNumber: orderNumber,
        warehouseId: warehouseId,
        carrierId: carrierId,
      })
    }
    setProductToReturn(productSelectionArray)
  }

  const sendProductsToReturn = async () => {
    // we prepare the data to send, the send it and check if response is ok, then we download the recieved file
    setIsDownloadLoading(true)
    const productData = []

    productToReturn.map((product) => {
      productData.push({
        lizeeSKU: product.Sku,
        quantity: product.quantity,
        orderToken: product.orderToken,
      })
    })

    const products = {
      products: productData,
      gender: user?.user?.gender,
      warehouseId: productToReturn[0].warehouseId,
      carrierId: productToReturn[0].carrierId,
      omsOrderId: productToReturn[0].orderNumber,
      rightOfWithdrawal: true,
    }

    const data = await postProductToReturn(products)
    if (data.type === 'POST_PRODUCT_TO_RETURN_SUCCESS') {
      if (data?.response[0]?.pdfUrl) {
        await setDownloadLink(data?.response[0]?.pdfUrl)
        await downloadURI(
          data.response[0].pdfUrl,
          `${process.env.STORE}_${data.response[0].trackingCode}`
        )
        await setIsDownloadLoading(false)
      }
    } else {
      setIsDownloadLoading(false)
    }
  }

  return (
    <div className={classes.root}>
      <Typography
        variant={'h5'}
        component={'h2'}
        className={classes.takeBackListTitle}
      >
        <CMSText
          asText
          data={takeback?.data?.order_list_title}
          defaultText={t('texts:account:order_list_title')}
        />
      </Typography>

      {activeOrder && (
        <Grid container spacing={1} className={classes.orderDetailsRowActions}>
          <Grid item xs={3} md={6}>
            <Button
              size={'small'}
              className={classes.simpleButton}
              onClick={() => handleBackToOrders()}
              startIcon={<KeyboardBackspaceIcon />}
              disableRipple
            >
              <CMSText
                asText
                data={account?.data?.order_details_close}
                defaultText={t('texts:account:order_details_close')}
              />
            </Button>
          </Grid>
          <Grid
            item
            xs={9}
            md={6}
            alignItems={'center'}
            alignContent={'flex-end'}
          >
            <Box textAlign={'right'}>
              {isModifying ? (
                <Button
                  size={'small'}
                  className={clsx(
                    classes.simpleButton,
                    classes.simpleButtonDanger
                  )}
                  onClick={() => cancelReturn()}
                  disableRipple
                >
                  <CMSText
                    asText
                    data={account?.data?.order_details_manage_cancel}
                    defaultText={t('texts:account:order_details_manage_cancel')}
                  />
                </Button>
              ) : (
                <Button
                  size={'small'}
                  className={clsx(
                    classes.simpleButton,
                    classes.simpleButtonPrimary
                  )}
                  onClick={() => setIsModifying(true)}
                  disableRipple
                >
                  <CMSText
                    asText
                    data={account?.data?.order_details_manage}
                    defaultText={t('texts:account:order_details_manage')}
                  />
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      )}

      {orders.map((order, index) => {
        const lastOrder = index + 1 === orders.length
        const orderDate = dayjs(order.checkoutCompletedAt).format('L')

        const isDisabled = isOrderReturnable.find(
          (orderInfos) => orderInfos.orderId === order.number
        )?.returnable
        const TakeBackOrderItem = () => (
          <>
            <div className={classes.takeBackListHeader}>
              {!activeOrder && <div className={classes.circle}></div>}
              <div className={clsx(classes.takeBackOrder)}>
                <Typography
                  variant={'body1'}
                  color={'primary'}
                  className={classes.takeBackItemTitle}
                >
                  <CMSText
                    asText
                    data={takeback?.data?.take_back_command}
                    defaultText={t('texts:takeBack:take_back_command')}
                  />
                  &nbsp;
                  {order.number}
                </Typography>
                <Typography variant={'body1'}>
                  <strong>
                    <Price amount={order.totals.total} />
                  </strong>
                </Typography>
              </div>
            </div>
            <div
              className={clsx(
                classes.takeBackItem,
                lastOrder && classes.takeBackItemLast,
                activeOrder && classes.takeBackItemActive
              )}
            >
              <Typography variant={'body2'} color={'textSecondary'}>
                {orderDate}
              </Typography>
              {order.items.map((item, index) => {
                return item.units.map((unit) => {
                  const isReturnable = isOrderReturnable.find(
                    (isReturnableInfos) =>
                      isReturnableInfos.orderId === order.number
                  )
                  const isSelected = productToReturn.find(
                    (product) => product.id === index
                  )
                    ? true
                    : false
                  return (
                    <>
                      <div
                        className={classes.productCard}
                        key={`${unit}-${index}`}
                      >
                        {activeOrder && (
                          <div
                            className={clsx(
                              classes.productCardCheckbox,
                              'checkbox'
                            )}
                          >
                            <Fade in={isModifying}>
                              <Checkbox
                                checked={isSelected}
                                name={item.name}
                                data-testid={`order__checkbox--${index}`}
                                disableRipple
                                size="small"
                                disabled={!isReturnable.returnable}
                                onChange={() =>
                                  handleProductSelection(
                                    item.product.variants[0].sku,
                                    index,
                                    1,
                                    order?.tokenValue,
                                    item,
                                    order.number,
                                    warehouseId,
                                    order.shipments[0].method.carrierId
                                  )
                                }
                              />
                            </Fade>
                          </div>
                        )}
                        <div className={classes.productCardImg}>
                          <ImgKitHandler
                            classes={classes.imgProduct}
                            imagePaths={item.product.images[0]}
                            settings={{
                              width: 50,
                              ar: '1-1',
                              defaultImage: main?.data?.default_image,
                            }}
                          />
                        </div>
                        <div className={classes.productCardContent}>
                          <div className={classes.productName}>
                            {item.product.name}
                            <>&nbsp;-&nbsp;</>
                            {item.product.variants[0].size}
                          </div>
                          <div className={classes.productName}>
                            {states[`${unit.productCondition}`]?.title}
                          </div>
                          <div className={classes.productName}>
                            <Price amount={item.total} />
                          </div>
                        </div>
                      </div>
                      <div className={classes.downloadInvoiceButton}>
                        <DownloadInvoiceButton
                          accountRes={account}
                          orderToken={order.tokenValue}
                          t={t}
                          handleDownloadInvoice={handleDownloadInvoice}
                          invoiceDownloadError={invoiceDownloadError}
                          isSub={isSubscription}
                        />
                      </div>
                    </>
                  )
                })
              })}
              {activeOrder && (
                <Box
                  textAlign={'center'}
                  className={classes.returnCtaContainer}
                >
                  {productSelection && downloadLink === '' ? (
                    <Button
                      data-testid={'downloadBtn'}
                      variant="contained"
                      color="primary"
                      disabled={isDownloadLoading || !productSelection}
                      onClick={() => sendProductsToReturn()}
                    >
                      <span
                        className={
                          isDownloadLoading ? classes.hide : classes.show
                        }
                      >
                        <CMSText
                          asText
                          data={account?.data?.order_details_generate}
                          defaultText={t(
                            'texts:account:order_details_generate'
                          )}
                        />
                      </span>
                      {isDownloadLoading && <Loader />}
                    </Button>
                  ) : (
                    downloadLink !== '' && (
                      <DownloadLink
                        downloadLink={downloadLink}
                        data={account?.data?.order_details_download}
                        defaultText={t('texts:account:order_details_download')}
                      />
                    )
                  )}
                </Box>
              )}

              {!activeOrder && (
                <div className={classes.orderDetailsRow}>
                  <MainTooltip
                    placement={'top'}
                    title={!isDisabled ? tooltipWithdrawalText : ''}
                    arrow
                  >
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        onClick={() => handleActiveCurrentOrder(index)}
                        className={clsx(
                          classes.orderDetailsBtn,
                          'btn--order-details'
                        )}
                        disabled={!isDisabled}
                      >
                        <CMSText
                          asText
                          data={account?.data?.order_details_open}
                          defaultText={t('texts:account:order_details_open')}
                        />
                      </Button>
                    </span>
                  </MainTooltip>
                </div>
              )}
            </div>
          </>
        )
        if (indexOrder === null) {
          return (
            <div key={index}>
              <TakeBackOrderItem />
            </div>
          )
        } else {
          if (indexOrder === index) {
            return activeOrderLoading ? (
              <div className={classes.loaderContainer}>
                <Loader />
              </div>
            ) : (
              <TakeBackOrderItem />
            )
          }
        }
      })}
    </div>
  )
}

export default connect(mapStateToProps, null)(TakeBackOrderList)
